import { useEffect, useState } from "react";
import { registerSW } from "virtual:pwa-register";

/**
 * Service worker lifecycle.
 *
 * Registration is what makes the till work offline, so it happens on every
 * load. But an update is OFFERED, never applied: reloading the terminal to
 * activate a new worker while someone is mid-sale would discard the cart and,
 * worse, interrupt a tender. Staff choose the moment.
 *
 * The offer is also suppressed while a cart is in progress, so the prompt
 * cannot appear over the tender screen.
 */

interface UpdatePromptProps {
  /** True while a sale is being built or paid — never interrupt that. */
  busy?: boolean;
}

export function UpdatePrompt({ busy = false }: UpdatePromptProps) {
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [update, setUpdate] = useState<(() => Promise<void>) | null>(null);

  useEffect(() => {
    const updateSW = registerSW({
      onNeedRefresh() {
        setNeedsRefresh(true);
      },
      onOfflineReady() {
        setOfflineReady(true);
      },
      onRegisteredSW(_url, registration) {
        // Check hourly. A café runs for twelve hours; checking on load alone
        // means a fix shipped at 09:00 waits until tomorrow.
        if (registration) {
          setInterval(() => void registration.update(), 60 * 60 * 1000);
        }
      },
    });

    setUpdate(() => () => updateSW(true));
  }, []);

  // Confirmation that the till will now survive an outage. Shown once, briefly.
  useEffect(() => {
    if (!offlineReady) return;
    const timer = setTimeout(() => setOfflineReady(false), 6000);
    return () => clearTimeout(timer);
  }, [offlineReady]);

  if (offlineReady) {
    return (
      <div className="swtoast" role="status">
        <span className="swtoast__dot" aria-hidden="true" />
        Ready to work offline
      </div>
    );
  }

  if (!needsRefresh || busy || !update) return null;

  return (
    <div className="swtoast swtoast--action" role="status">
      <span>An update is ready</span>
      <button type="button" onClick={() => void update()}>
        Restart now
      </button>
      <button type="button" className="swtoast__later" onClick={() => setNeedsRefresh(false)}>
        Later
      </button>
    </div>
  );
}

export default UpdatePrompt;
